import { useCallback, useEffect, useState } from 'react'
import { useMutation, useQuery } from '@tanstack/react-query'
import { useSelector } from 'react-redux'
import { DEFAULT_STALE_DATA_TIMEOUT } from '@AppState/app-config'
import { SearchServiceAPI } from '@API/data-services/search-service'
import { RootState } from '@AppState/redux-store'
import { AdminSearchConfiguration, SearchCollection } from '@Generated/back-office-client/src'
import { SearchConfigurationView } from './search-configuration-view'

export const SearchConfiguration = () => {
  const { activeCustomerId } = useSelector((state: RootState) => state.users)
  const [currentCollection, setCurrentCollection] = useState<SearchCollection | null>(null)
  const [pendingSelectHostname, setPendingSelectHostname] = useState<string | null>(null)

  const { data: searchCollections } = useQuery({
    queryKey: [`${activeCustomerId}-search-collections`],
    staleTime: DEFAULT_STALE_DATA_TIMEOUT,
    queryFn: async () => {
      return SearchServiceAPI.getCollections()
    },
  })

  const { data: overrides, refetch: refetchSearchOverrides } = useQuery({
    queryKey: [
      `${activeCustomerId}-${currentCollection ? currentCollection.hostname : ''}-search-overrides`,
    ],
    staleTime: DEFAULT_STALE_DATA_TIMEOUT,
    queryFn: async () => {
      return SearchServiceAPI.getSearchOverrides(currentCollection!.hostname)
    },
    enabled: !!currentCollection,
  })

  const { data: adminSearchConfig, refetch: refetchAdminSearchConfig } = useQuery({
    queryKey: [
      `${activeCustomerId}-${currentCollection ? currentCollection.hostname : ''}-admin-search-config`,
    ],
    staleTime: DEFAULT_STALE_DATA_TIMEOUT,
    queryFn: async () => {
      return SearchServiceAPI.getAdminSearchConfig(currentCollection!.hostname)
    },
    enabled: !!currentCollection,
  })

  const refetchSearchOverridesWithDelay = useCallback(() => {
    refetchSearchOverrides()
    // sometimes we need to give typesense a while for all their nodes to be updated with the config changes
    setTimeout(() => {
      refetchSearchOverrides()
    }, 3000)
  }, [refetchSearchOverrides])

  const { mutate: deleteSearchOverride } = useMutation({
    mutationFn: (overrideName: string) => {
      return SearchServiceAPI.deleteSearchOverride(overrideName, currentCollection!.hostname)
    },
    onSuccess: () => {
      return refetchSearchOverridesWithDelay()
    },
  })

  const { mutate: syncAdminSearchConfig } = useMutation({
    mutationFn: (adminSearchConfig: AdminSearchConfiguration) => {
      return SearchServiceAPI.saveAdminSearchConfig(currentCollection!.hostname, adminSearchConfig)
    },
    onSuccess: () => {
      return refetchAdminSearchConfig()
    },
  })

  useEffect(() => {
    if (searchCollections?.length && !currentCollection) {
      setCurrentCollection(searchCollections[0])
    }
  }, [currentCollection, searchCollections])

  useEffect(() => {
    if (!pendingSelectHostname || !searchCollections) {
      return
    }
    const match = searchCollections.find(
      (collection) => collection.hostname === pendingSelectHostname,
    )
    if (match) {
      setCurrentCollection(match)
      setPendingSelectHostname(null)
    }
  }, [pendingSelectHostname, searchCollections])

  return (
    <SearchConfigurationView
      searchCollections={searchCollections}
      currentCollection={currentCollection}
      onSelectCollection={setCurrentCollection}
      adminSearchConfig={adminSearchConfig}
      onSaveAdminConfig={syncAdminSearchConfig}
      overrides={overrides}
      onDeleteOverride={deleteSearchOverride}
      onUpsertOverrideSuccess={refetchSearchOverridesWithDelay}
      onCreated={setPendingSelectHostname}
    />
  )
}
